import * as React from 'react';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { ThunderboltOutlined } from '@ant-design/icons';
import { AppStoreUploadArtifactStatus, DsymUploadArtifactStatus } from '@/types';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';

// Helper functions for upload status display
export const statusTagText = (status: AppStoreUploadArtifactStatus | DsymUploadArtifactStatus): string => {
    switch (status) {
        case AppStoreUploadArtifactStatus.AppStoreUploadNotStarted:
        case DsymUploadArtifactStatus.DsymUploadNotStarted:
            return 'NOT STARTED';
        case AppStoreUploadArtifactStatus.AppStoreUploadPending:
        case DsymUploadArtifactStatus.DsymUploadPending:
            return 'PENDING';
        case AppStoreUploadArtifactStatus.AppStoreUploadRetryRequested:
            return 'RETRY REQUESTED';
        case AppStoreUploadArtifactStatus.AppStoreUploadInProgress:
        case DsymUploadArtifactStatus.DsymUploadInProgress:
            return 'IN PROGRESS';
        case AppStoreUploadArtifactStatus.AppStoreUploadRetrying:
        case DsymUploadArtifactStatus.DsymUploadRetrying:
            return 'RETRYING';
        case AppStoreUploadArtifactStatus.AppStoreUploadSuccess:
        case DsymUploadArtifactStatus.DsymUploadSuccess:
            return 'COMPLETE';
        case AppStoreUploadArtifactStatus.AppStoreUploadError:
        case DsymUploadArtifactStatus.DsymUploadError:
            return 'FAILED';
        case AppStoreUploadArtifactStatus.AppStoreUploadBlocked:
            return 'BLOCKED';
        default:
            return 'UNKNOWN';
    }
};

export const statusTagColor = (status: AppStoreUploadArtifactStatus | DsymUploadArtifactStatus): string => {
    switch (status) {
        case AppStoreUploadArtifactStatus.AppStoreUploadNotStarted:
        case DsymUploadArtifactStatus.DsymUploadNotStarted:
        case AppStoreUploadArtifactStatus.AppStoreUploadPending:
        case DsymUploadArtifactStatus.DsymUploadPending:
        case AppStoreUploadArtifactStatus.AppStoreUploadRetryRequested:
        case AppStoreUploadArtifactStatus.AppStoreUploadInProgress:
        case DsymUploadArtifactStatus.DsymUploadInProgress:
        case AppStoreUploadArtifactStatus.AppStoreUploadRetrying:
        case DsymUploadArtifactStatus.DsymUploadRetrying:
            return runwayColors.statusYellow;
        case AppStoreUploadArtifactStatus.AppStoreUploadSuccess:
        case DsymUploadArtifactStatus.DsymUploadSuccess:
            return runwayColors.statusGreen;
        case AppStoreUploadArtifactStatus.AppStoreUploadError:
        case DsymUploadArtifactStatus.DsymUploadError:
        case AppStoreUploadArtifactStatus.AppStoreUploadBlocked:
            return runwayColors.statusRed;
        default:
            return runwayColors.statusGrey;
    }
};

interface UploadStatusSectionProps {
    children: React.ReactNode;
    style?: React.CSSProperties;
}

/**
 * A reusable container component for displaying upload status information.
 * Provides consistent styling with a header containing "UPLOAD STATUS" and a lightning bolt icon.
 */
export const UploadStatusSection: React.FC<UploadStatusSectionProps> = ({ children, style }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                borderRadius: 8,
                borderStyle: 'solid',
                borderWidth: 1,
                backgroundColor: 'white',
                borderColor: antGrey.grey4,
                ...style,
            }}
        >
            <Space style={{ width: '100%', padding: 12 }} align="start" direction="vertical" size={12}>
                <Space size={8} align="center">
                    <ThunderboltOutlined style={{ color: runwayColors.primary }} />
                    <Text
                        style={{
                            fontSize: 10,
                            color: antGrey.grey7,
                            fontWeight: 500,
                            letterSpacing: '0.1em',
                        }}
                    >
                        UPLOAD STATUS
                    </Text>
                </Space>
                {children}
            </Space>
        </div>
    );
};
